import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import SEO from "../components/SEO";
import spaciousGodownImg from "../assets/images/authentic/warehouse_spacious_godown.webp";
import transportLogisticsImg from "../assets/images/authentic/wool_transport_logistics.webp";
import {
  PackageCheck,
  Award,
  Sliders,
  CheckCircle2,
  ArrowRight,
  Phone,
  MessageCircle,
  Truck,
  Factory,
  Scale,
  CalendarCheck,
} from "lucide-react";

const BulkWoolSupply = () => {
  const { lPath, isRTL } = useLanguage();

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://skwoolens.trivanahospitality.com/en"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Bulk Wool Supply",
        "item": "https://skwoolens.trivanahospitality.com/en/bulk-wool-supply"
      }
    ]
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Bulk Raw Wool Supply & Mill Contracts",
    "provider": {
      "@type": "WholesaleStore",
      "name": "S.K. Woollens",
      "telephone": "+91-7014581250",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "B-26, Un Mandi, Antyodaya Nagar",
        "addressLocality": "Bikaner",
        "addressRegion": "Rajasthan",
        "postalCode": "334001",
        "addressCountry": "IN"
      }
    },
    "description": "Multi-tonne bulk raw wool supply and continuous delivery contracts for commercial yarn spinning mills across India and international buyers.",
    "areaServed": ["IN", "Worldwide"]
  };

  const schema = [breadcrumbSchema, serviceSchema];

  return (
    <div className="space-y-16 sm:space-y-24 py-6">
      <SEO
        title="Bulk Wool Supplier in India | Multi-Tonne Mill Supply | S.K. Woollens"
        description="Looking for bulk wool supply in India? S.K. Woollens provides multi-tonne raw wool contracts, compressed bales (30+ & 32-34+ micron) & buffer godown storage."
        keywords="bulk wool supplier in India, bulk wool supply, industrial raw wool supply, raw wool for spinning mills, bulk carpet wool, B2B wool contracts India"
        canonicalUrl="https://skwoolens.trivanahospitality.com/en/bulk-wool-supply"
        schema={schema}
      />

      {/* 1. Header Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-wool-sand/50 via-white to-wool-sand/30 border border-wool-border rounded-3xl p-8 sm:p-14 shadow-subtle">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-4">
              <div className="wool-badge inline-flex">
                <PackageCheck className="w-3.5 h-3.5 text-wool-bronze" />
                <span>Commercial Multi-Tonne Mill Supply</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-wool-charcoal tracking-tight font-display">
                Bulk Wool Supplier for Commercial Mills
              </h1>
              <p className="text-base sm:text-lg text-wool-slate leading-relaxed">
                Engineered for high-volume yarn manufacturers and industrial textile mills requiring recurring monthly tonnages, consistent fibre lots, and zero operational downtime. S.K. Woollens delivers reliable multi-tonne bulk wool supply backed by six decades of trading capacity.
              </p>

              <div className="p-4 bg-white/80 border border-wool-border rounded-xl text-xs sm:text-sm text-wool-slate">
                <span className="font-semibold text-wool-bronze block mb-1">
                  Contract Stability & Buffer Storage:
                </span>
                We maintain expansive godowns in Bikaner to store buffer stock, shielding your spinning lines from raw wool seasonal price spikes and local supply shortages.
              </div>

              <div className="pt-2 flex flex-wrap items-center gap-3">
                <a
                  href="https://wa.me/917014581250?text=Hi%2C%20I%20would%20like%20to%20discuss%20bulk%20wool%20supply%20contracts%20for%20our%20spinning%20mill"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  <MessageCircle className="w-4 h-4 text-emerald-300" />
                  <span>Enquire for Bulk Supply Contract</span>
                  <ArrowRight className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`} />
                </a>

                <a href="tel:+917014581250" className="btn-secondary flex items-center gap-2">
                  <Phone className="w-4 h-4 text-wool-bronze" />
                  <span>Call +91 7014581250</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="relative rounded-2xl overflow-hidden shadow-xl border border-wool-border bg-white">
                <img
                  src={spaciousGodownImg}
                  alt="Spacious Bulk Wool Godown Facility in Bikaner - S.K. Woollens"
                  className="w-full h-72 sm:h-80 object-cover"
                  loading="eager"
                />
                <div className="p-3 bg-wool-sand/80 border-t border-wool-border text-center text-xs font-semibold text-wool-charcoal">
                  Spacious Storage Capacity for Multi-Tonne Raw Wool Batches
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Bulk Supply Core Pillars */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white border border-wool-border rounded-3xl p-6 sm:p-10 shadow-subtle space-y-6">
          <div className="max-w-3xl space-y-3">
            <div className="wool-badge">
              <Factory className="w-3.5 h-3.5 text-wool-bronze" />
              <span>Industrial Reliability</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-wool-charcoal font-display">
              Why Large Spinning Mills Partner with S.K. Woollens for Bulk Supply
            </h2>
            <p className="text-sm sm:text-base text-wool-slate leading-relaxed">
              When a spinning mill processes 20 to 100 tonnes of wool monthly, raw material consistency is essential. Fibre diameter drift or variable vegetable matter can severely impair carding speed and finished yarn tensile strength. Our bulk supply framework is structured around commercial predictability:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
            <div className="wool-card p-6 space-y-3 bg-wool-sand/20">
              <Sliders className="w-6 h-6 text-wool-bronze" />
              <h3 className="text-base font-bold text-wool-charcoal">Batch-to-Batch Homogeneity</h3>
              <p className="text-xs text-wool-slate leading-relaxed">
                We blend and sort clips systematically so that every truckload matches previous shipments in micron, staple length, and clean yield percentage.
              </p>
            </div>

            <div className="wool-card p-6 space-y-3 bg-wool-sand/20">
              <CalendarCheck className="w-6 h-6 text-wool-bronze" />
              <h3 className="text-base font-bold text-wool-charcoal">Scheduled Delivery Cadence</h3>
              <p className="text-xs text-wool-slate leading-relaxed">
                Consignments dispatched on strict weekly or bi-weekly schedules aligned with your mill production plan, optimizing your cash flow and warehouse space.
              </p>
            </div>

            <div className="wool-card p-6 space-y-3 bg-wool-sand/20">
              <Scale className="w-6 h-6 text-wool-bronze" />
              <h3 className="text-base font-bold text-wool-charcoal">Certified Weighing & Moisture Standards</h3>
              <p className="text-xs text-wool-slate leading-relaxed">
                All bulk bales are weighed at authorized weighbridges with clear tare slips, moisture verification, and proper weatherproofing during transport.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Specifications Handled in Bulk */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto space-y-3 mb-10">
          <div className="wool-badge">
            <Award className="w-3.5 h-3.5 text-wool-bronze" />
            <span>Commercial Specifications</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-wool-charcoal font-display">
            Bulk Wool Specifications Handled
          </h2>
          <p className="text-sm sm:text-base text-wool-muted">
            Specialized coarse grades tailored for woolen and semi-worsted spinning installations.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <div className="bg-white border border-wool-border rounded-2xl p-5 shadow-sm space-y-2">
            <span className="text-[11px] font-bold text-wool-bronze uppercase">Fibre Diameter</span>
            <h4 className="text-base font-bold text-wool-charcoal">30+ & 32–34+ Micron</h4>
            <p className="text-xs text-wool-muted">
              Specialized for resilient carpet yarns and heavy industrial woolen applications.
            </p>
          </div>

          <div className="bg-white border border-wool-border rounded-2xl p-5 shadow-sm space-y-2">
            <span className="text-[11px] font-bold text-wool-bronze uppercase">Staple Length</span>
            <h4 className="text-base font-bold text-wool-charcoal">50mm – 100mm+</h4>
            <p className="text-xs text-wool-muted">
              Consistent staple lengths to minimize carding drop-out and maximize spinning efficiency.
            </p>
          </div>

          <div className="bg-white border border-wool-border rounded-2xl p-5 shadow-sm space-y-2">
            <span className="text-[11px] font-bold text-wool-bronze uppercase">Condition</span>
            <h4 className="text-base font-bold text-wool-charcoal">Greasy Bales / Scoured</h4>
            <p className="text-xs text-wool-muted">
              Compressed raw greasy bales for self-scouring mills or pre-scoured lots ready for carding.
            </p>
          </div>

          <div className="bg-white border border-wool-border rounded-2xl p-5 shadow-sm space-y-2">
            <span className="text-[11px] font-bold text-wool-bronze uppercase">Minimum Order</span>
            <h4 className="text-base font-bold text-wool-charcoal">Commercial Batches</h4>
            <p className="text-xs text-wool-muted">
              From initial trial truckloads to recurring multi-tonne monthly contracts.
            </p>
          </div>
        </div>
      </section>

      {/* 4. Logistics & Loading Infrastructure */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-wool-charcoal text-white rounded-3xl p-6 sm:p-12 shadow-2xl">
          <div className="lg:col-span-6 space-y-4">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/10 text-wool-sand text-xs font-bold uppercase tracking-wider rounded-full">
              <Truck className="w-3.5 h-3.5 text-wool-sand" />
              <span>Full-Truckload Logistics</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight font-display">
              Reliable Logistics Directly to Your Mill Gate
            </h2>
            <p className="text-xs sm:text-sm text-wool-sand/80 leading-relaxed">
              We manage dedicated truckload dispatches across major Indian spinning corridors including Bhadohi, Panipat, Mirzapur, Ludhiana, Jaipur, and Jamnagar. Our loading bays are equipped to handle high-density hydraulic bales efficiently, ensuring clean transit and on-time arrival.
            </p>
            <ul className="space-y-2 text-xs text-wool-sand/90 pt-2">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span>Heavy-duty tarpaulin wrapping to prevent moisture absorption in transit</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span>Weighbridge slips and transit insurance documentation included</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span>Live dispatch tracking and delivery coordination with mill managers</span>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-6 relative rounded-2xl overflow-hidden border border-white/20">
            <img
              src={transportLogisticsImg}
              alt="Bulk Raw Wool Transport Truckload Logistics - S.K. Woollens"
              className="w-full h-72 sm:h-80 object-cover"
              loading="lazy"
            />
            <div className="p-3 bg-black/60 backdrop-blur-md border-t border-white/20 text-center text-xs text-wool-sand">
              Truckload Loading & B2B Mill Transport from Bikaner
            </div>
          </div>
        </div>
      </section>

      {/* 5. Related Pages Navigation */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="border border-wool-border bg-white rounded-3xl p-6 sm:p-10 shadow-subtle space-y-6">
          <h3 className="text-xl font-bold text-wool-charcoal font-display">
            Explore Related Solutions
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to={lPath("/for-yarn-manufacturers")}
              className="p-4 border border-wool-border rounded-xl hover:border-wool-bronze hover:bg-wool-sand/20 transition flex flex-col justify-between"
            >
              <div>
                <h4 className="text-sm font-bold text-wool-charcoal">For Yarn Manufacturers</h4>
                <p className="text-xs text-wool-muted mt-1">Spinning mill raw wool solutions matching technical sheets.</p>
              </div>
              <span className="text-xs font-semibold text-wool-bronze mt-3 inline-flex items-center gap-1">
                Read more <ArrowRight className="w-3 h-3" />
              </span>
            </Link>

            <Link
              to={lPath("/carpet-wool-supplier-india")}
              className="p-4 border border-wool-border rounded-xl hover:border-wool-bronze hover:bg-wool-sand/20 transition flex flex-col justify-between"
            >
              <div>
                <h4 className="text-sm font-bold text-wool-charcoal">Carpet Wool Supplier India</h4>
                <p className="text-xs text-wool-muted mt-1">High-crimp coarse fibres for carpet yarn spinners.</p>
              </div>
              <span className="text-xs font-semibold text-wool-bronze mt-3 inline-flex items-center gap-1">
                Read more <ArrowRight className="w-3 h-3" />
              </span>
            </Link>

            <Link
              to={lPath("/wool-supplier-bikaner")}
              className="p-4 border border-wool-border rounded-xl hover:border-wool-bronze hover:bg-wool-sand/20 transition flex flex-col justify-between"
            >
              <div>
                <h4 className="text-sm font-bold text-wool-charcoal">Wool Supplier in Bikaner</h4>
                <p className="text-xs text-wool-muted mt-1">Direct mandi gate procurement and storage at Un Mandi B-26.</p>
              </div>
              <span className="text-xs font-semibold text-wool-bronze mt-3 inline-flex items-center gap-1">
                Read more <ArrowRight className="w-3 h-3" />
              </span>
            </Link>

            <Link
              to={lPath("/wool-agent-bikaner")}
              className="p-4 border border-wool-border rounded-xl hover:border-wool-bronze hover:bg-wool-sand/20 transition flex flex-col justify-between"
            >
              <div>
                <h4 className="text-sm font-bold text-wool-charcoal">Wool Agent in Bikaner</h4>
                <p className="text-xs text-wool-muted mt-1">On-the-ground commission representation, lot testing & mandi brokerage.</p>
              </div>
              <span className="text-xs font-semibold text-wool-bronze mt-3 inline-flex items-center gap-1">
                Read more <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* 6. Direct Contact CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-r from-wool-sand via-wool-sand-dark to-wool-sand border border-wool-border rounded-3xl p-8 sm:p-12 text-center space-y-6 shadow-premium">
          <div className="max-w-2xl mx-auto space-y-2">
            <h3 className="text-2xl sm:text-3xl font-extrabold text-wool-charcoal font-display">
              Initiate a Bulk Wool Supply Discussion
            </h3>
            <p className="text-xs sm:text-sm text-wool-slate">
              Contact Nikhil Banthia or Sunil Banthia to review your mill's upcoming monthly wool requirements and receive formal commercial terms.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <a
              href="https://wa.me/917014581250?text=Hi%2C%20I%20would%20like%20to%20discuss%20bulk%20wool%20supply%20contracts"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-2"
            >
              <MessageCircle className="w-4 h-4 text-emerald-300" />
              <span>WhatsApp Bulk Wool Desk</span>
            </a>

            <a
              href="tel:+917014581250"
              className="btn-secondary flex items-center gap-2 bg-white"
            >
              <Phone className="w-4 h-4 text-wool-bronze" />
              <span>Call Nikhil Banthia: +91 7014581250</span>
            </a>

            <Link to={lPath("/contact")} className="btn-secondary bg-white">
              <span>View Contact Desk</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BulkWoolSupply;
